import React, { useContext } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { Navigate, Outlet } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';

/**
 * Loading spinner pour les routes
 */
const LoadingSpinner = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
    <CircularProgress />
  </Box>
);

/**
 * Vérifie si l'utilisateur a un rôle admin (admin ou super_admin)
 */
const isAdminRole = (role) => role === 'admin' || role === 'super_admin';

/**
 * Route publique - accessible uniquement aux utilisateurs NON connectés
 * Si connecté, redirige vers le dashboard approprié selon le rôle
 */
export const PublicRoute = () => {
  const { user, loading } = useContext(AuthContext);

  if (loading) return <LoadingSpinner />;

  // Si connecté, rediriger selon le rôle
  if (user) {
    if (isAdminRole(user.role)) {
      return <Navigate to="/admin" replace />;
    }
    if (user.role === 'coach') {
      return <Navigate to="/coach" replace />;
    }
    // Client/User - nouveau portail mobile-first
    return <Navigate to="/client" replace />;
  }

  return <Outlet />;
};

/**
 * Route utilisateur (client) - accessible aux utilisateurs connectés avec rôle 'user'
 */
export const UserRoute = () => {
  const { user, loading } = useContext(AuthContext);

  if (loading) return <LoadingSpinner />;

  if (!user) {
    return <Navigate to="/" replace />;
  }

  // Admin et Coach ne doivent pas accéder aux routes user
  if (isAdminRole(user.role)) {
    return <Navigate to="/admin" replace />;
  }
  if (user.role === 'coach') {
    return <Navigate to="/coach" replace />;
  }

  return <Outlet />;
};

/**
 * Route client - accessible aux clients connectés
 * Redirige vers le nouveau portail client mobile-first
 */
export const ClientRoute = () => {
  const { user, loading } = useContext(AuthContext);

  if (loading) return <LoadingSpinner />;

  if (!user) {
    return <Navigate to="/" replace />;
  }

  // Admin et Coach ne doivent pas accéder aux routes client
  if (isAdminRole(user.role)) {
    return <Navigate to="/admin" replace />;
  }
  if (user.role === 'coach') {
    return <Navigate to="/coach" replace />;
  }

  return <Outlet />;
};

/**
 * Route administrateur - accessible aux utilisateurs avec rôle 'admin' ou 'super_admin'
 */
export const AdminRoute = () => {
  const { user, loading } = useContext(AuthContext);

  if (loading) return <LoadingSpinner />;

  if (!user) {
    return <Navigate to="/admin-login" replace />;
  }

  if (!isAdminRole(user.role)) {
    if (user.role === 'coach') {
      return <Navigate to="/coach" replace />;
    }
    return <Navigate to="/week" replace />;
  }

  return <Outlet />;
};

/**
 * Route coach - accessible aux coachs (abonnés ou non)
 * Utilisé pour les routes comme checkout qui ne nécessitent pas d'abonnement
 */
export const CoachRoute = () => {
  const { user, loading } = useContext(AuthContext);

  if (loading) return <LoadingSpinner />;

  if (!user) {
    return <Navigate to="/" replace />;
  }

  if (user.role !== 'coach') {
    if (isAdminRole(user.role)) {
      return <Navigate to="/admin" replace />;
    }
    return <Navigate to="/week" replace />;
  }

  return <Outlet />;
};

/**
 * Route coach - accessible à tous les coachs
 * Tous les coachs ont au minimum le plan free
 */
export const SubscribedCoachRoute = () => {
  const { user, loading } = useContext(AuthContext);

  if (loading) return <LoadingSpinner />;

  if (!user) {
    return <Navigate to="/" replace />;
  }

  if (user.role !== 'coach') {
    if (isAdminRole(user.role)) {
      return <Navigate to="/admin" replace />;
    }
    return <Navigate to="/week" replace />;
  }

  return <Outlet />;
};

const ProtectedRoutes = { PublicRoute, UserRoute, ClientRoute, AdminRoute, CoachRoute, SubscribedCoachRoute };
export default ProtectedRoutes;
